import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ArrowLeft } from 'lucide-react';
import { feedbackSessionFormInFocus } from '../../../actions/feedbackFormInFocusAction';
import { RootState } from '../../../reducers';

const sessionDetailSchema = yup.object({
	bookingId: yup.number(),
	ratedParticipantId: yup.number(),
	programme: yup.object().nullable().required('This field is required'),
	sessionWith: yup.object().nullable().required('This field is required'),
	sessionType: yup.number().nullable().required('This field is required'),
	sessionDate: yup.date().nullable().required('This field is required'),
	sessionTime: yup.date().nullable().required('This field is required'),
	sessionDuration: yup.object().nullable().required('This field is required'),
	sessionService: yup.object().nullable().required('This field is required'),
});

const formatDate = (value: unknown) => {
	if (!value) {
		return '';
	}

	const date = new Date(value as string | Date);
	if (Number.isNaN(date.getTime())) {
		return '';
	}

	return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`;
};

const formatTime = (value: unknown) => {
	if (!value) {
		return '';
	}

	const date = new Date(value as string | Date);
	if (Number.isNaN(date.getTime())) {
		return '';
	}

	return `${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;
};

export default function SessionDetailForm(props: any) {
	const { programData = [], groupData = [], handleProgrammeChange, serviceData = [], sessionDurationData = [], onSubmit } = props;
	const programAnswer = useSelector((state: RootState) => state.programAnswer);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<any>({
		defaultValues: programAnswer?.sessionDetailForm,
		resolver: yupResolver(sessionDetailSchema),
	});

	const routeToSessionMenu = () => {
		dispatch(feedbackSessionFormInFocus(null));
		navigate('../feedback-session');
	};

	return (
		<form className="mx-auto w-full max-w-3xl overflow-x-hidden" onSubmit={handleSubmit(onSubmit)}>
			<header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
				<button
					aria-label="Back to feedback sessions"
					className="inline-flex rounded-md p-2 text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:hidden"
					onClick={routeToSessionMenu}
					type="button"
				>
					<ArrowLeft aria-hidden="true" className="h-5 w-5" />
				</button>
				<h1 className="session-detail min-w-0 flex-1 text-center text-lg font-semibold text-slate-900 md:text-left">Session Details</h1>
				<button className="hidden rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/80 md:inline-flex" type="submit">
					Continue
				</button>
			</header>

			<div className="space-y-7 px-5 py-6">
				<div>
					<label className="select-programme mb-2 block text-sm font-medium text-slate-800" htmlFor="programme">Select Programme</label>
					<Controller
						control={control}
						name="programme"
						render={({ field }) => (
							<select
								className="h-11 min-w-0 w-full max-w-full rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
								id="programme"
								onChange={(event) => {
									const programme = programData.find((item: any) => `${item.value}` === event.target.value) || null;
									field.onChange(programme);
									if (programme) {
										handleProgrammeChange(programme.value);
									}
								}}
								value={field.value?.value ?? ''}
							>
								<option value="">Please select programme</option>
								{programData.map((item: any) => <option key={item.value} value={item.value}>{item.label}</option>)}
							</select>
						)}
					/>
					{errors.programme && <p className="session-details-text-error mt-1 text-sm text-destructive">{errors.programme.message as string}</p>}
				</div>

				<div>
					<label className="session-with mb-2 block text-sm font-medium text-slate-800" htmlFor="session-with">Who is the mentor / mentee?</label>
					<Controller
						control={control}
						name="sessionWith"
						render={({ field }) => (
							<select
								className="h-11 min-w-0 w-full max-w-full rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
								id="session-with"
								onChange={(event) => field.onChange(groupData.find((item: any) => `${item.value.group_id}` === event.target.value) || null)}
								value={field.value?.value?.group_id ?? ''}
							>
								<option value="">Please select</option>
								{groupData.map((item: any) => <option key={item.value.group_id} value={item.value.group_id}>{item.label}</option>)}
							</select>
						)}
					/>
					{errors.sessionWith && <p className="session-details-text-error mt-1 text-sm text-destructive">{errors.sessionWith.message as string}</p>}
				</div>

				<fieldset>
					<legend className="session-place mb-2 text-sm font-medium text-slate-800">Where did the session take place?</legend>
					<Controller
						control={control}
						name="sessionType"
						render={({ field }) => (
							<div className="radio-button-session-type flex gap-5">
								{[[0, 'Online'], [1, 'Physical']].map(([value, label]) => (
									<label className="inline-flex items-center gap-2 text-sm text-slate-700" key={value as number}>
										<input checked={field.value === value} name={field.name} onChange={() => field.onChange(value)} type="radio" />
										{label}
									</label>
								))}
							</div>
						)}
					/>
					{errors.sessionType && <p className="session-details-text-error mt-1 text-sm text-destructive">{errors.sessionType.message as string}</p>}
				</fieldset>

				<div>
					<label className="session-date-input mb-2 block text-sm font-medium text-slate-800" htmlFor="session-date">When did the session happen?</label>
					<Controller
						control={control}
						name="sessionDate"
						render={({ field }) => <input className="h-11 min-w-0 w-full max-w-full rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" id="session-date" onChange={(event) => field.onChange(event.target.value ? new Date(`${event.target.value}T00:00:00`) : null)} type="date" value={formatDate(field.value)} />}
					/>
					{errors.sessionDate && <p className="session-details-text-error mt-1 text-sm text-destructive">{errors.sessionDate.message as string}</p>}
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<div className="min-w-0">
						<label className="select-programme mb-2 block text-sm font-medium text-slate-800" htmlFor="session-time">Session Time</label>
						<Controller
							control={control}
							name="sessionTime"
							render={({ field }) => <input className="h-11 min-w-0 w-full max-w-full rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" id="session-time" onChange={(event) => { const [hours, minutes] = event.target.value.split(':').map(Number); const time = new Date(); time.setHours(hours || 0, minutes || 0, 0, 0); field.onChange(event.target.value ? time : null); }} type="time" value={formatTime(field.value)} />}
						/>
						{errors.sessionTime && <p className="session-details-text-error mt-1 text-sm text-destructive">{errors.sessionTime.message as string}</p>}
					</div>
					<div className="min-w-0">
						<label className="select-programme mb-2 block text-sm font-medium text-slate-800" htmlFor="session-duration">Session Duration</label>
						<Controller control={control} name="sessionDuration" render={({ field }) => <select className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" id="session-duration" onChange={(event) => field.onChange(sessionDurationData.find((item: any) => `${item.value}` === event.target.value) || null)} value={field.value?.value ?? ''}><option value="">Please select</option>{sessionDurationData.map((item: any) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>} />
						{errors.sessionDuration && <p className="session-details-text-error mt-1 text-sm text-destructive">{errors.sessionDuration.message as string}</p>}
					</div>
				</div>

				<div>
					<label className="select-programme mb-2 block text-sm font-medium text-slate-800" htmlFor="session-service">Mentor Services</label>
					<Controller control={control} name="sessionService" render={({ field }) => <select className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" id="session-service" onChange={(event) => field.onChange(serviceData.find((item: any) => `${item.value}` === event.target.value) || null)} value={field.value?.value ?? ''}><option value="">Please select</option>{serviceData.map((item: any) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>} />
					{errors.sessionService && <p className="session-details-text-error mt-1 text-sm text-destructive">{errors.sessionService.message as string}</p>}
				</div>
			</div>

			<div className="px-5 pb-6 md:hidden">
				<button className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" type="submit">Continue</button>
			</div>
		</form>
	);
}
